import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import ListingCard from "../components/ListingCard";
import CityPicker from "../components/CityPicker";
import { useLocation as useAJLocation } from "../lib/location";
import { getGeoPosition, geoErrorMessage } from "../lib/geolocation";
import usePullToRefresh from "../lib/usePullToRefresh";
import PullIndicator from "../components/PullIndicator";
import { Funnel, X, Truck, CurrencyDollar, Sparkle, Star, Calendar, MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";

const SORT_OPTIONS = [
  { key: "relevante", label: "Relevante", icon: Sparkle },
  { key: "menor_preco", label: "Menor preço", icon: CurrencyDollar },
  { key: "melhor_avaliado", label: "Melhor avaliado", icon: Star },
];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const { location: globalLoc, setLocation } = useAJLocation();
  const [categories, setCategories] = useState([]);
  // V18.1 — hidrata itens/loading do cache da última busca (evita blink "sem anúncios" ao voltar)
  const [items, setItems] = useState(() => {
    try {
      const raw = sessionStorage.getItem("aj_search_cache_v1");
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed?.items) ? parsed.items : [];
    } catch { return []; }
  });
  const [loading, setLoading] = useState(() => {
    try { return !sessionStorage.getItem("aj_search_cache_v1"); } catch { return true; }
  });
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);
  const PAGE_SIZE = 20;
  const [showFilters, setShowFilters] = useState(false);

  const [cityState, setCityState] = useState({ city: params.get("city") || globalLoc?.city || "", uf: globalLoc?.uf || "MT" });
  const [category, setCategory] = useState(params.get("category") || "");
  const [catSearch, setCatSearch] = useState("");
  const [maxPrice, setMaxPrice] = useState(params.get("max_price") || "");
  const [sortBy, setSortBy] = useState("relevante");
  const [onlyEntrega, setOnlyEntrega] = useState(false);
  // Filtro por RAIO (geográfico) — vive no contexto de localização (compartilhado c/ Header)
  const radius = (globalLoc?.radius_km && globalLoc?.radius_lat != null)
    ? { lat: globalLoc.radius_lat, lng: globalLoc.radius_lng, radius_km: globalLoc.radius_km }
    : null;
  const setRadiusVal = (r) => setLocation(r
    ? { radius_km: r.radius_km, radius_lat: r.lat, radius_lng: r.lng }
    : { radius_km: null, radius_lat: null, radius_lng: null });
  const [nearestBeyond, setNearestBeyond] = useState(null);

  // Presets de raio de 1 toque — usa o centro atual (raio/localização) ou pede geolocalização.
  const applyPreset = (km) => {
    const apply = (lat, lng) => setLocation({ radius_km: km, radius_lat: lat, radius_lng: lng });
    if (radius) return apply(radius.lat, radius.lng);
    if (globalLoc?.lat && globalLoc?.lng) return apply(globalLoc.lat, globalLoc.lng);
    // V18.9.5 — usa wrapper (native/plugin no app, navigator no web)
    getGeoPosition({ timeout: 12000 })
      .then((p) => apply(p.coords.latitude, p.coords.longitude))
      .catch((e) => toast.warning(geoErrorMessage(e)));
  };
  // V7.5: filtros de disponibilidade por período
  const [availableToday, setAvailableToday] = useState(false);
  const [filterStart, setFilterStart] = useState(params.get("start_date") || "");
  const [filterEnd, setFilterEnd] = useState(params.get("end_date") || "");

  const formatDate = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("-");
  return `${day}/${month}/${year}`;
};

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data.all || r.data));
  }, []);

  const q = params.get("q") || "";

  const buildParams = (overrides = {}) => {
    const p = {
      q: overrides.q ?? q,
      city: overrides.city ?? cityState.city,
      category: overrides.category ?? category,
      max_price: overrides.max_price ?? maxPrice,
    };
    // V12.9: ordenação e filtro de entrega aplicados no SERVIDOR (sobre todo o catálogo)
    const sb = overrides.sort ?? sortBy;
    if (sb && sb !== "relevante") p.sort = sb;
    if (overrides.only_entrega ?? onlyEntrega) p.only_entrega = true;
    // V7.5: disponibilidade por período
    const sd = overrides.start_date ?? filterStart;
    const ed = overrides.end_date ?? filterEnd;
    if (sd && ed) {
      p.start_date = sd;
      p.end_date = ed;
    } else if (overrides.available_today ?? availableToday) {
      p.available_today = true;
    }
    if (globalLoc?.lat && globalLoc?.lng) {
      p.user_lat = globalLoc.lat;
      p.user_lng = globalLoc.lng;
    }
    // Filtro por raio: centro + raio_km sobrepõem coords/cidade (geográfico puro)
    const rad = overrides.radius ?? radius;
    if (rad && rad.radius_km && rad.lat != null && rad.lng != null) {
      p.user_lat = rad.lat;
      p.user_lng = rad.lng;
      p.radius_km = rad.radius_km;
      delete p.city;
    }
    return Object.fromEntries(Object.entries(p).filter(([, v]) => v));
  };

  const search = async (overrides = {}) => {
    setLoading(true);
    const clean = buildParams(overrides);
    try {
      const res = await api.get("/listings", { params: { ...clean, limit: PAGE_SIZE, offset: 0 } });
      const data = res.data;
      setItems(data);
      setHasMore(data.length === PAGE_SIZE);
      // Cabeçalho: item mais próximo além do raio (para sugerir aumentar o raio)
      const nb = res.headers?.["x-nearest-beyond-km"];
      setNearestBeyond(clean.radius_km && nb ? parseFloat(nb) : null);
      // V18.1 — cacheia último resultado (query + itens) para hidratar ao voltar
      try { sessionStorage.setItem("aj_search_cache_v1", JSON.stringify({ query: clean, items: data, hasMore: data.length === PAGE_SIZE, ts: Date.now() })); } catch { /* ignore */ }
    } catch (e) {
      // V18.1 — em erro NÃO zera a lista atual (evita "sem anúncios" ao voltar/rede piscar)
      console.warn("[search] falha ao buscar:", e?.message);
    } finally {
      setLoading(false);
    }
    const urlClean = { ...clean };
    delete urlClean.user_lat; delete urlClean.user_lng; delete urlClean.radius_km;
    setParams(urlClean);
  };

  // V12.8: carrega +20 ao rolar (paginação por offset no servidor)
  const loadMore = async () => {
    if (loadingMore || loading || !hasMore) return;
    setLoadingMore(true);
    try {
      const clean = buildParams();
      const { data } = await api.get("/listings", { params: { ...clean, limit: PAGE_SIZE, offset: items.length } });
      setItems((prev) => [...prev, ...data]);
      setHasMore(data.length === PAGE_SIZE);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMore(); },
      { rootMargin: "320px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, hasMore, loadingMore, loading]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { search(); }, [q, category, cityState.city, availableToday, filterStart, filterEnd, sortBy, onlyEntrega, globalLoc?.radius_km, globalLoc?.radius_lat, globalLoc?.radius_lng, globalLoc?.lat, globalLoc?.lng]);

  // Sincroniza a cidade da Busca com o seletor global do topo (Header).
  // Usa ref para reagir SÓ à troca real de cidade — não reverte um "limpar cidade" feito pelo usuário.
  const prevGlobalCity = useRef(globalLoc?.city);
  useEffect(() => {
    if (globalLoc?.city && globalLoc.city !== prevGlobalCity.current) {
      prevGlobalCity.current = globalLoc.city;
      setCityState({ city: globalLoc.city, uf: globalLoc.uf || "MT" });
    }
  }, [globalLoc?.city, globalLoc?.uf]);

  // Ordenação e filtro "só entrega" agora vêm do servidor (sobre todo o catálogo).
  const displayedItems = items;
  const ptr = usePullToRefresh(() => search());

  return (
    <div className="max-w-7xl mx-auto px-4 py-4" data-testid="search-page">
      <PullIndicator pulling={ptr.isPulling} progress={ptr.progress} refreshing={ptr.isRefreshing} />
      {/* Categories: campo de busca + chips filtráveis */}
      <div className="mb-2">
        <input
          value={catSearch}
          onChange={(e) => setCatSearch(e.target.value)}
          placeholder="Buscar categoria..."
          className="aj-input h-9 text-sm"
          data-testid="search-category-filter"
        />
      </div>
      <div className="flex items-center gap-2 overflow-x-auto pb-2 -mx-4 px-4 mb-3" data-testid="search-categories">
        <button
          onClick={() => setCategory("")}
          className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-bold border-2 ${category === "" ? "border-[#004E98] bg-[#004E98] text-white" : "border-slate-200 bg-white text-slate-700"}`}
        >
          Todas
        </button>
        {categories
          .filter((c) => c.toLowerCase().includes(catSearch.trim().toLowerCase()))
          .map((c) => (
          <button
            key={c}
            onClick={() => setCategory(c)}
            className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-bold border-2 ${category === c ? "border-[#004E98] bg-[#004E98] text-white" : "border-slate-200 bg-white text-slate-700"}`}
            data-testid={`cat-chip-${c}`}
          >
            {c}
          </button>
        ))}
        {categories.filter((c) => c.toLowerCase().includes(catSearch.trim().toLowerCase())).length === 0 && (
          <span className="text-xs text-slate-400 px-1">Nenhuma categoria encontrada.</span>
        )}
      </div>

      {/* V11.4 (item 6): seletor de período EM DESTAQUE — escolher quando quero alugar
          aplica o filtro de disponibilidade na hora, sem precisar abrir "Mais". */}
      <div className="aj-card p-3 mb-3 bg-[#004E98]/5 border-[#004E98]/20 flex flex-col sm:flex-row sm:items-end gap-2" data-testid="search-period-quick">
        <div className="flex-1 min-w-0">
          <label className="text-[11px] font-bold text-[#004E98] flex items-center gap-1"><Calendar size={13} weight="bold" /> Quero alugar de</label>
          <input
            type="date"
            value={filterStart}
            min={new Date().toISOString().split("T")[0]}
            onChange={(e) => { setFilterStart(e.target.value); setAvailableToday(false); }}
            className="aj-input mt-1"
            data-testid="quick-period-start"
          />
        </div>
        <div className="flex-1 min-w-0">
          <label className="text-[11px] font-bold text-[#004E98]">até</label>
          <input
            type="date"
            value={filterEnd}
            min={filterStart || new Date().toISOString().split("T")[0]}
            onChange={(e) => { setFilterEnd(e.target.value); setAvailableToday(false); }}
            className="aj-input mt-1"
            data-testid="quick-period-end"
          />
        </div>
        {(filterStart || filterEnd) && (
          <button
            onClick={() => { setFilterStart(""); setFilterEnd(""); }}
            className="text-xs text-slate-500 underline whitespace-nowrap pb-2.5"
            data-testid="quick-period-clear"
          >
            Limpar
          </button>
        )}
      </div>

      {/* Sort + filter row */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 -mx-4 px-4 mb-3">
        {SORT_OPTIONS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setSortBy(key)}
            className={`inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-bold border-2 ${sortBy === key ? "border-[#FF6B35] bg-[#FF6B35]/10 text-[#FF6B35]" : "border-slate-200 bg-white text-slate-700"}`}
            data-testid={`sort-${key}`}
          >
            <Icon size={13} weight="bold" /> {label}
          </button>
        ))}
        <button
          onClick={() => {
            const next = !availableToday;
            setAvailableToday(next);
            if (next) { setFilterStart(""); setFilterEnd(""); }
          }}
          className={`inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-bold border-2 ${availableToday ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-700"}`}
          data-testid="filter-available-today"
        >
          <Calendar size={13} weight="bold" /> Disponível hoje
        </button>
        <button
          onClick={() => setOnlyEntrega(!onlyEntrega)}
          className={`inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-bold border-2 ${onlyEntrega ? "border-green-600 bg-green-50 text-green-700" : "border-slate-200 bg-white text-slate-700"}`}
          data-testid="filter-entrega"
        >
          <Truck size={13} weight="bold" /> Entrega Disponivel
        </button>
        <button onClick={() => setShowFilters(true)} className="inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-bold border-2 border-[#004E98] bg-[#004E98] text-white" data-testid="open-filters-btn">
          <Funnel size={13} weight="bold" /> Mais
        </button>
      </div>

      {/* Presets de raio de 1 toque (grandes centros) */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 -mx-4 px-4 mb-3" data-testid="radius-presets">
        <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 whitespace-nowrap">
          <MapPin size={13} weight="fill" className="text-[#FF6B35]" /> Raio:
        </span>
        {[5, 15, 30].map((km) => (
          <button
            key={km}
            onClick={() => applyPreset(km)}
            className={`inline-flex items-center gap-1 whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-bold border-2 ${radius?.radius_km === km ? "border-[#FF6B35] bg-[#FF6B35]/10 text-[#FF6B35]" : "border-slate-200 bg-white text-slate-700"}`}
            data-testid={`radius-preset-${km}`}
          >
            {km} km
          </button>
        ))}
        {radius?.radius_km && (
          <button
            onClick={() => setRadiusVal(null)}
            className="inline-flex items-center gap-1 whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-bold border-2 border-slate-200 bg-white text-slate-500"
            data-testid="radius-preset-clear"
          >
            <X size={12} weight="bold" /> Limpar
          </button>
        )}
      </div>

      {(filterStart && filterEnd) && (
  <div className="aj-card p-2 mb-3 bg-emerald-50 border-emerald-200 text-emerald-800 text-xs flex items-center justify-between" data-testid="chip-period">
    <span className="flex items-center gap-1.5">
      <Calendar size={12} weight="bold" /> 
      Período: <b>{formatDate(filterStart)}</b> até <b>{formatDate(filterEnd)}</b>
    </span>
    <button onClick={() => { setFilterStart(""); setFilterEnd(""); }} className="text-emerald-700 hover:text-red-600">
      <X size={14} weight="bold" />
    </button>
  </div>
)}

      {((cityState.city && !radius) || maxPrice) && (
        <div className="flex flex-wrap gap-2 mb-4">
          {cityState.city && !radius && (
            <button onClick={() => { setCityState({ city: "", uf: "MT" }); search({ city: "" }); }} className="aj-badge inline-flex items-center gap-1" data-testid="chip-city">
              {cityState.city} <X size={10} weight="bold" />
            </button>
          )}
          {maxPrice && (
            <button onClick={() => { setMaxPrice(""); search({ max_price: "" }); }} className="aj-badge inline-flex items-center gap-1" data-testid="chip-max-price">
              Até R$ {maxPrice} <X size={10} weight="bold" />
            </button>
          )}
        </div>
      )}

      {radius?.radius_km && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid="chip-radius-wrap">
          <button
            onClick={() => setRadiusVal(null)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border-2 border-[#FF6B35] bg-[#FF6B35]/10 text-[#FF6B35]"
            data-testid="chip-radius"
          >
            <MapPin size={13} weight="fill" />
            {cityState.city || globalLoc?.city || "Minha região"} · até {radius.radius_km} km
            <X size={11} weight="bold" />
          </button>
        </div>
      )}

      {radius?.radius_km && !loading && displayedItems.length === 0 && nearestBeyond != null && (
        <div className="aj-card p-4 mb-4 bg-amber-50 border-amber-200 text-amber-900 text-sm" data-testid="radius-beyond-empty">
          Nenhum item dentro de <b>{radius.radius_km} km</b>. O mais próximo está a cerca de <b>{nearestBeyond} km</b>.
          <button onClick={() => setRadiusVal({ ...radius, radius_km: Math.min(150, Math.ceil(nearestBeyond)) })} className="ml-1 underline font-bold" data-testid="radius-expand-to-nearest">
            Aumentar o raio para ~{Math.min(150, Math.ceil(nearestBeyond))} km
          </button>
        </div>
      )}
      {radius?.radius_km && !loading && displayedItems.length > 0 && nearestBeyond != null && (
        <div className="text-xs text-amber-700 mb-3 flex items-center gap-1.5" data-testid="radius-beyond-note">
          <MapPin size={12} weight="fill" /> Há mais itens um pouco além do raio (o mais próximo a ~{nearestBeyond} km).
          <button onClick={() => setRadiusVal({ ...radius, radius_km: Math.min(150, Math.ceil(nearestBeyond)) })} className="underline font-bold" data-testid="radius-expand-note">
            Aumentar raio
          </button>
        </div>
      )}

      <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">
        {displayedItems.length}{hasMore ? "+" : ""} resultado(s) {q && `· "${q}"`}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="search-skeleton">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="aj-card overflow-hidden animate-pulse">
              <div className="aspect-square bg-slate-200" />
              <div className="p-2.5 space-y-2">
                <div className="h-3 bg-slate-200 rounded w-3/4" />
                <div className="h-3 bg-slate-200 rounded w-1/2" />
                <div className="h-4 bg-slate-200 rounded w-1/3" />
              </div>
            </div>
          ))}
        </div>
      ) : displayedItems.length === 0 ? (
        <div className="aj-card p-10 text-center text-slate-500">Nenhuma ferramenta encontrada.</div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {displayedItems.map((it) => <ListingCard key={it.id} item={it} />)}
        </div>
      )}

      {!loading && displayedItems.length > 0 && hasMore && (
        <div ref={sentinelRef} className="py-8 text-center text-sm text-slate-400" data-testid="search-load-more-sentinel">
          {loadingMore ? "Carregando mais..." : "Role para carregar mais"}
        </div>
      )}
      {!loading && !hasMore && displayedItems.length > 0 && (
        <div className="py-8 text-center text-xs text-slate-400" data-testid="search-end-of-list">Você viu todos os itens.</div>
      )}

      {showFilters && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center md:justify-center pb-safe" onClick={(e) => e.target === e.currentTarget && setShowFilters(false)}>
          <div className="bg-white w-full md:max-w-md md:rounded-2xl rounded-t-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-xl">Filtros</h3>
              <button onClick={() => setShowFilters(false)}><X size={22} weight="bold" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Cidade</span>
                <CityPicker value={cityState} onChange={(v) => { setCityState(v); if (radius) setRadiusVal(null); }} testId="filter-city-picker" placeholder="Selecione a cidade" enableRadius radiusValue={radius} onRadius={(r) => { setRadiusVal(r); setShowFilters(false); }} />
                {radius?.radius_km && (
                  <button
                    type="button"
                    onClick={() => setRadiusVal(null)}
                    className="mt-1.5 text-[11px] text-red-600 font-bold"
                    data-testid="filter-clear-radius"
                  >
                    Remover raio (até {radius.radius_km} km)
                  </button>
                )}
              </div>
              <label className="block">
                <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Preço máximo (R$)</span>
                <input type="number" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} className="aj-input mt-1" data-testid="filter-max-price" />
              </label>
              {/* V7.5: Filtro de disponibilidade por intervalo de datas */}
              <div className="aj-card p-3 bg-emerald-50/30 border-emerald-100" data-testid="filter-period-block">
                <div className="flex items-center gap-1.5 mb-2 text-sm font-bold text-emerald-900">
                  <Calendar size={14} weight="bold" /> Disponível no período
                </div>
                <p className="text-[11px] text-slate-600 mb-2">
                  Mostre apenas itens com estoque livre para esse intervalo.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block">
                    <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Início</span>
                    <input
                      type="date"
                      value={filterStart}
                      onChange={(e) => { setFilterStart(e.target.value); setAvailableToday(false); }}
                      className="aj-input mt-1 text-sm"
                      data-testid="filter-start-date"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Fim</span>
                    <input
                      type="date"
                      value={filterEnd}
                      min={filterStart || undefined}
                      onChange={(e) => { setFilterEnd(e.target.value); setAvailableToday(false); }}
                      className="aj-input mt-1 text-sm"
                      data-testid="filter-end-date"
                    />
                  </label>
                </div>
                {(filterStart || filterEnd) && (
                  <button type="button" onClick={() => { setFilterStart(""); setFilterEnd(""); }} className="text-[11px] text-red-600 mt-2" data-testid="filter-clear-period">
                    Limpar período
                  </button>
                )}
              </div>
            </div>
            <button onClick={() => { search(); setShowFilters(false); }} className="btn-primary w-full mt-4" data-testid="apply-filters-btn">
              Aplicar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
